/**
 * SaaS Permission System for React Customer App
 * Manages hierarchical security group permissions with automatic user assignment
 */

// Permission hierarchy and definitions
export const PERMISSIONS = {
    // Basic user permissions (all authenticated users)
    BASIC_USER_FEATURES: 'basic_user_features',
    READ_ONLY_ACCESS: 'read_only_access',
    
    // Partner permissions (includes basic_user permissions)  
    PARTNER_FEATURES: 'partner_features',
    
    // Admin permissions (includes all permissions)
    FULL_ACCESS: 'full_access',
    USER_MANAGEMENT: 'user_management',
    SYSTEM_CONFIG: 'system_config',
    
    // SLA permissions
    VIEW_SLA_DATA: 'view_sla_data'
};

// Security groups with hierarchical permissions
export const SECURITY_GROUPS = {
    admin: {
        name: 'admin',
        displayName: 'Administrator', 
        permissions: [
            PERMISSIONS.FULL_ACCESS,
            PERMISSIONS.USER_MANAGEMENT, 
            PERMISSIONS.SYSTEM_CONFIG,
            PERMISSIONS.PARTNER_FEATURES,
            PERMISSIONS.BASIC_USER_FEATURES,
            PERMISSIONS.READ_ONLY_ACCESS
        ]
    },
    partner: {
        name: 'partner',
        displayName: 'Partner',
        permissions: [
            PERMISSIONS.PARTNER_FEATURES,
            PERMISSIONS.BASIC_USER_FEATURES,
            PERMISSIONS.READ_ONLY_ACCESS
        ]
    },
    basic_user: {
        name: 'basic_user', 
        displayName: 'User',
        permissions: [
            PERMISSIONS.BASIC_USER_FEATURES,
            PERMISSIONS.READ_ONLY_ACCESS
        ]
    },
    read_only: {
        name: 'read_only',
        displayName: 'Read Only',
        permissions: [
            PERMISSIONS.READ_ONLY_ACCESS
        ]
    }
};

/**
 * Permission Manager Class
 * Handles user permission checking and API integration
 */
export class PermissionManager {
    constructor() {
        this.userPermissions = [];
        this.userGroups = [];
        
        // Use dynamic configuration from deployment (window._env_) first, then fallback to build-time env vars
        const envConfig = window._env_ || {};
        this.enviroment = envConfig.enviroment || process.env.REACT_APP_enviroment || '';
        // const branchCode = envConfig.BRANCH_CODE || process.env.REACT_APP_BRANCH || '1000';
        
        this.apiBaseUrl = null;
        this.initPromise = this.initialize();
    }

    async initialize() {
        // Use centralized configuration - use customer portal APIM for permissions
        const { getApiEndpoint } = require('./centralizedConfig');
        this.apiBaseUrl = await getApiEndpoint('customer-portal');
        return this.apiBaseUrl;
    }

    async ensureInitialized() {
        if (!this.apiBaseUrl) {
            await this.initPromise;
        }
    }

    /**
     * Initialize permissions for the current user
     * @param {object} msalInstance - MSAL instance for authentication
     * @returns {Promise<boolean>} Success status
     */
    async initializeUserPermissions(msalInstance) {
        try {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                console.warn('⚠️ No authenticated user found');
                return false;
            }

            const user = accounts[0];
            const userId = user.localAccountId;

            // Get user permissions from API
            const permissions = await this.fetchUserPermissions(userId, msalInstance);
            if (permissions) {
                this.userPermissions = permissions.permissions || [];
                this.userGroups = permissions.groups || [];
                
                console.log(`User permissions initialized:`, {
                    userId: userId,
                    groups: this.userGroups,
                    permissions: this.userPermissions
                });
                
                return true;
            }
            
            return false;
            
        } catch (error) {
            console.error('❌ Error initializing user permissions:', error);
            return false;
        }
    }

    /**
     * Fetch user permissions from the API
     * @param {string} userId - User identifier 
     * @param {object} msalInstance - Optional MSAL instance (unused, kept for backward compatibility)
     * @returns {Promise<object|null>} User permissions or null
     */
    async fetchUserPermissions(userId, msalInstance = null) {
        try {
            // Ensure service is initialized
            await this.ensureInitialized();
            
            // Use centralized API client instead of creating new MSAL instance
            const { getApiClient } = await import('./apiClient');
            const apiClient = getApiClient();
            
            // Ensure API client is ready (it should already be initialized with MSAL at app startup)
            if (!apiClient.isReady()) {
                console.warn('⚠️ API client not ready, waiting for authentication...');
                const isReady = await apiClient.waitForAuthentication(5000);
                if (!isReady) {
                    throw new Error('API client authentication not ready');
                }
            }

            // Use apiClient to make authenticated request
            const response = await apiClient.apiRequest(`/v1/security-groups/user-permissions/${userId}`, {
                method: 'GET'
            });

            if (response && response.success !== false) {
                // Response is successful (apiClient returns data directly on success)
                return response.data || response;
            } else {
                console.warn(`⚠️ Failed to fetch permissions: ${response?.error || 'Unknown error'}`);
                
                // Fallback: assume basic_user permissions for authenticated users
                return {
                    groups: ['basic_user'],
                    permissions: SECURITY_GROUPS.basic_user.permissions
                };
            }
            
        } catch (error) {
            console.error('❌ Error fetching user permissions:', error);
            
            // Fallback: assume basic_user permissions
            return {
                groups: ['basic_user'],
                permissions: SECURITY_GROUPS.basic_user.permissions
            };
        }
    }

    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission to check
     * @returns {boolean} Has permission
     */
    hasPermission(permission) {
        return this.userPermissions.includes(permission);
    }

    /**
     * Check if user belongs to a specific security group
     * @param {string} groupName - Group to check
     * @returns {boolean} Is member of group
     */
    isInGroup(groupName) {
        return this.userGroups.includes(groupName);
    }

    /**
     * Check if user has any of the specified permissions
     * @param {Array<string>} permissions - Permissions to check
     * @returns {boolean} Has at least one permission
     */
    hasAnyPermission(permissions) {
        return permissions.some(permission => this.hasPermission(permission));
    }

    /**
     * Check if user has all specified permissions
     * @param {Array<string>} permissions - Permissions to check
     * @returns {boolean} Has all permissions
     */
    hasAllPermissions(permissions) {
        return permissions.every(permission => this.hasPermission(permission));
    }

    /**
     * Get user's highest security group (for display purposes)
     * @returns {object} Security group info
     */
    getHighestGroup() {
        // Check groups in hierarchy order
        const groupHierarchy = ['admin', 'partner', 'basic_user', 'read_only'];
        
        for (const groupName of groupHierarchy) {
            if (this.isInGroup(groupName)) {
                return SECURITY_GROUPS[groupName];
            }
        }
        
        // Default to basic_user for authenticated users
        return SECURITY_GROUPS.basic_user;
    }

    /**
     * Get all user's security groups
     * @returns {Array<object>} Security group info
     */
    getAllGroups() {
        return this.userGroups.map(groupName => SECURITY_GROUPS[groupName]).filter(Boolean);
    }
}

// Global permission manager instance
let permissionManager = null;

/**
 * Get or create global permission manager instance
 * @returns {PermissionManager} Permission manager instance
 */
export function getPermissionManager() {
    if (!permissionManager) {
        permissionManager = new PermissionManager();
    }
    return permissionManager;
}

/**
 * Initialize permission system for the app
 * @param {object} msalInstance - MSAL instance
 * @returns {Promise<PermissionManager>} Initialized permission manager
 */
export async function initializePermissions(msalInstance) {
    const manager = getPermissionManager();
    await manager.initializeUserPermissions(msalInstance);
    return manager;
}

/**
 * Permission-based conditional rendering hook
 * @param {string|Array<string>} requiredPermissions - Required permissions
 * @param {string} mode - 'any' or 'all' (default: 'any')
 * @returns {boolean} Should render component
 */
export function usePermission(requiredPermissions, mode = 'any') {
    const manager = getPermissionManager();
    
    if (!requiredPermissions) return true;
    
    const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    
    if (mode === 'all') {
        return manager.hasAllPermissions(permissions);
    } else {
        return manager.hasAnyPermission(permissions);
    }
}

/**
 * Permission Gate Component
 * Conditionally renders children based on user permissions
 */
export function PermissionGate({ children, requiredPermissions, mode = 'any', fallback = null }) {
    const hasPermission = usePermission(requiredPermissions, mode);
    
    if (hasPermission) {
        return children;
    }
    
    return fallback;
}

/**
 * Security Group Badge Component
 * Displays user's security group with styling
 */
export function SecurityGroupBadge({ className = '' }) {
    const manager = getPermissionManager();
    const group = manager.getHighestGroup();
    
    const badgeStyles = {
        admin: 'bg-red-100 text-red-800 border-red-200',
        partner: 'bg-blue-100 text-blue-800 border-blue-200', 
        basic_user: 'bg-green-100 text-green-800 border-green-200',
        read_only: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    const style = badgeStyles[group.name] || badgeStyles.basic_user;
    
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${style} ${className}`}>
            {group.displayName}
        </span>
    );
}

/**
 * User Permissions Display Component
 * Shows all user permissions for debugging/admin purposes
 */
export function UserPermissionsDisplay({ className = '' }) {
    const manager = getPermissionManager();
    
    return (
        <div className={`p-4 bg-gray-50 rounded-lg ${className}`}>
            <h3 className="text-sm font-medium text-gray-900 mb-2">User Permissions</h3>
            
            <div className="mb-3">
                <p className="text-xs text-gray-600 mb-1">Security Groups:</p>
                <div className="flex flex-wrap gap-1">
                    {manager.getAllGroups().map(group => (
                        <span key={group.name} className="text-xs px-2 py-1 bg-white rounded border">
                            {group.displayName}
                        </span>
                    ))}
                </div>
            </div>
            
            <div>
                <p className="text-xs text-gray-600 mb-1">Permissions:</p>
                <div className="flex flex-wrap gap-1">
                    {manager.userPermissions.map(permission => (
                        <span key={permission} className="text-xs px-2 py-1 bg-white rounded border text-gray-700">
                            {permission.replace(/_/g, ' ')}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

const permissionsExport = {
    PermissionManager,
    getPermissionManager,
    initializePermissions,
    usePermission,
    PermissionGate,
    SecurityGroupBadge,
    UserPermissionsDisplay,
    PERMISSIONS,
    SECURITY_GROUPS
};

export default permissionsExport;